import { parseContextSourceFieldValueMonikerId } from '~/components/v2/components/workspace/workspace-agent-panel/adapters/durable-plain-text/contextSourceFieldValueMonikerId';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { markWorkspaceAgentContentSynthesizedMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/markWorkspaceAgentContentSynthesizedMonikerPill';
import {
  type PillMonikerSegment,
  splitPillMonikers,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';
import {
  type WorkspaceAgentArtifactPillCitationType,
  type WorkspaceAgentEntityMonikerType,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentMonikerTypes';

const synthesizeArtifactPill = (
  citationType: WorkspaceAgentArtifactPillCitationType,
  artifactId: string,
): WorkspaceAgentMessagePill => {
  switch (citationType) {
    case 'assetList':
      return { count: 1, id: artifactId, type: 'assetList' };
    case 'boardList':
      return { count: 1, id: artifactId, type: 'boardList' };
    case 'changePreview':
      return { count: 0, id: artifactId, rows: [], title: '', truncated: false, type: 'changePreview' };
    case 'table':
      return { columns: [], count: 0, id: artifactId, rows: [], title: '', truncated: false, type: 'table' };
  }
};

type WorkspaceAgentEntityPillMonikerType = Exclude<WorkspaceAgentEntityMonikerType, 'skill'>;

const synthesizeEntityPill = (
  entityType: WorkspaceAgentEntityPillMonikerType,
  entityId: string,
): WorkspaceAgentMessagePill | undefined => {
  switch (entityType) {
    case 'asset':
      return { assetId: entityId, id: entityId, type: 'asset' };
    case 'assetVersion':
      // The moniker names the version. `assetId` repeats that id because the pill type requires one;
      // the chip loads the clip by `versionId`.
      return { assetId: entityId, id: entityId, type: 'asset', versionId: entityId };
    case 'board':
      return { boardId: entityId, id: entityId, type: 'board' };
    case 'comment':
      // The pill type requires a discussion, asset, and version. The moniker only names the comment,
      // so those ids repeat it until `Discussions.Comments.get` fills them in. Matching the comment id
      // is how the card knows it cannot open yet.
      return {
        assetId: entityId,
        authorName: '',
        body: '',
        commentId: entityId,
        createdAt: '',
        discussionId: entityId,
        id: entityId,
        type: 'comment',
        versionId: entityId,
      };
    case 'contextSourceFieldValue': {
      const parsed = parseContextSourceFieldValueMonikerId(entityId);

      if (!parsed) {
        return undefined;
      }

      return {
        contextSourceFieldValueId: parsed.contextSourceFieldValueId,
        contextSourceId: parsed.contextSourceId,
        id: entityId,
        type: 'contextSourceFieldValue',
      };
    }
    // No wire pill: history renders `PrivateWorkspaceAgentPanelMessageShareLinkMoniker` from markdown.
    // Automations readouts import this helper — share links stay plain text there too.
    case 'shareLink':
    // Same for showcases: `PrivateWorkspaceAgentPanelMessageShowcaseMoniker` renders from markdown.
    case 'showcase':
    // Same for content exporter zips: `PrivateWorkspaceAgentPanelMessageContentExporterMoniker` renders from markdown.
    case 'contentExporter':
      return undefined;
  }
};

/**
 * Skill instructions and automation readouts call this with no options. They have no agent
 * session, so artifact citations stay unmarked and render as count placeholders.
 */
export type SynthesizeWorkspaceAgentPillsFromContentOptions = {
  /** Agent-panel table cells: artifact citation placeholders should hydrate via session APIs. */
  markArtifactHydration?: boolean;
};

const pillFromMonikerSegment = (
  segment: PillMonikerSegment,
  options: SynthesizeWorkspaceAgentPillsFromContentOptions | undefined,
): WorkspaceAgentMessagePill | undefined => {
  if (segment.type !== 'pill') {
    return undefined;
  }

  let pill: WorkspaceAgentMessagePill | undefined;

  if (segment.citationType !== undefined) {
    pill = synthesizeArtifactPill(segment.citationType, segment.monikerId);
  } else if (segment.entityType !== undefined && segment.entityType !== 'skill') {
    pill = synthesizeEntityPill(segment.entityType, segment.monikerId);
  }

  if (pill) {
    markWorkspaceAgentContentSynthesizedMonikerPill(pill, options);
  }

  return pill;
};

const seenKeyForMonikerSegment = (segment: PillMonikerSegment): string | undefined => {
  if (segment.type !== 'pill') {
    return undefined;
  }

  const monikerType = segment.citationType ?? segment.entityType;

  return monikerType === undefined ? undefined : `${monikerType}:${segment.monikerId}`;
};

/**
 * Pills parsed from message content. `@[pill:<id>]` stays on `parsePills`. Artifact placeholders
 * are replaced once `Artifacts.Rows.list` returns. Entity monikers already carry the id to load.
 */
export const synthesizeWorkspaceAgentPillsFromContent = (
  content: string | null,
  options?: SynthesizeWorkspaceAgentPillsFromContentOptions,
): readonly WorkspaceAgentMessagePill[] => {
  if (!content) {
    return [];
  }

  const pills: WorkspaceAgentMessagePill[] = [];
  const seen = new Set<string>();

  for (const segment of splitPillMonikers(content)) {
    const pill = pillFromMonikerSegment(segment, options);
    const seenKey = seenKeyForMonikerSegment(segment);

    if (pill && seenKey && !seen.has(seenKey)) {
      seen.add(seenKey);
      pills.push(pill);
    }
  }

  return pills;
};
